import Button from "@material-ui/core/Button";
import clsx from "clsx";
import firebase from "firebase/app";
import { useTranslation } from "next-i18next";
import Image from "next/image";
import { useRouter } from "next/router";

import { RouteName } from "@/consts";
import { AnalyticsEventEnum } from "@/consts/AnalyticsEventEnum";
import { formatThousandsCountToNDecimal } from "@/features/common/helper/formatCount";
import { useUserContext } from "@/features/user/contexts/useUserContext";
import {
  ChannelFragment,
  UserFollowCountFragment,
  UserBasicInfoFragment,
} from "@/generated/generated-hooks";

import { ProfileGroupButtons } from "./ProfileGroupButtons";

type BannerInfoProps = {
  writer: UserBasicInfoFragment & UserFollowCountFragment;
  channel?: ChannelFragment;
};

export const BannerInfo: React.FC<BannerInfoProps> = (props) => {
  const { t } = useTranslation(["profilePublic", "common"]);

  const { writer, channel } = props;
  const { displayName, followerCount, followingCount, shortCaption, _id } =
    writer;

  const router = useRouter();
  const { user } = useUserContext();
  const isOwner = user?._id === _id;

  const onClickEditProfileButton = () => {
    firebase.analytics().logEvent(AnalyticsEventEnum.PublicProfileEditProfile);
    router.push(RouteName.MySettingsProfile);
  };

  const onClickFollowerOrFollowing = (routeName: RouteName) => {
    if (!isOwner) return null;
    return () => router.push(routeName);
  };

  return (
    <>
      <div
        className="flex flex-col flex-1 justify-end px-7 pb-3 min-w-0"
        data-test-id="BannerInfo-container"
      >
        <div className="flex mt-3">
          <span className="text-2xl text-white font-medium w-full truncate">
            {displayName}
          </span>
        </div>

        <div className="flex mt-3">
          <div
            className={clsx("flex items-center", isOwner && "touchable")}
            onClick={onClickFollowerOrFollowing(RouteName.MyAudienceUser)}
          >
            <span className="text-xl text-white font-medium">
              {formatThousandsCountToNDecimal(followerCount, 1)}
            </span>

            <span className="text-sm text-white ml-2">
              {t("common:follow.followers")}
            </span>
          </div>
          <div
            className={clsx("flex items-center ml-6", isOwner && "touchable")}
            onClick={onClickFollowerOrFollowing(RouteName.MyFollowingUser)}
          >
            <span className="text-xl text-white font-medium">
              {formatThousandsCountToNDecimal(followingCount, 1)}
            </span>

            <span className="text-sm text-white ml-2">
              {t("common:follow.following")}
            </span>
          </div>
        </div>

        {shortCaption && (
          <div className="flex mt-3 text-white">
            <p className="text-base truncate whitespace-pre-wrap">
              {shortCaption}
            </p>
          </div>
        )}
      </div>

      <div className="flex items-end my-3">
        {isOwner ? (
          <Button
            style={{ color: "white", borderColor: "white" }}
            onClick={onClickEditProfileButton}
            variant="outlined"
            size="large"
            startIcon={
              <Image
                alt="eidt profile icon"
                src="/assets/publicProfile/editWhite.png"
                layout="fixed"
                width={15}
                height={15}
              />
            }
          >
            {t("profilePublic:editProfileButtonLabel")}
          </Button>
        ) : (
          <ProfileGroupButtons writer={writer} channel={channel} />
        )}
      </div>
    </>
  );
};
